import { useCallback, useEffect, useState } from "react";
import { VenetianMask, Loader2, Search, Star, StarOff } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";

export const ViewAsDialog = ({ open, onOpenChange }) => {
  const { user } = useAuth();
  const canStaff = user?.role === "super_admin";
  const [q, setQ] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState("");

  const load = useCallback((term) => {
    setLoading(true);
    api.get(`/auth/masquerade/targets?q=${encodeURIComponent(term)}`)
      .then((r) => setData(r.data))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not load users"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const t = setTimeout(() => load(q), q ? 300 : 0);
    return () => clearTimeout(t);
  }, [open, q, load]);

  const start = async (u) => {
    setBusy(u.user_id);
    try {
      await api.post("/auth/masquerade/start", { target_user_id: u.user_id });
      window.location.href = "/dashboard";
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not start View as");
      setBusy("");
    }
  };

  const togglePin = async (u, e) => {
    e.stopPropagation();
    const pinned = (data?.pins || []).some((p) => p.user_id === u.user_id);
    const ids = pinned
      ? data.pins.filter((p) => p.user_id !== u.user_id).map((p) => p.user_id)
      : [...(data.pins || []).map((p) => p.user_id), u.user_id];
    if (ids.length > (data?.pin_limit || 6)) {
      toast.error(`You can pin up to ${data.pin_limit} shortcuts`);
      return;
    }
    try {
      await api.put("/auth/masquerade/pins", { user_ids: ids });
      load(q);
      toast.success(pinned ? `Unpinned ${u.name}` : `Pinned ${u.name} to your menu`);
      window.dispatchEvent(new Event("masquerade-pins-changed"));
    } catch {
      toast.error("Could not update shortcuts");
    }
  };

  const Row = ({ u, pinned }) => (
    <button type="button" onClick={() => start(u)} disabled={!!busy}
      data-testid={`view-as-row-${u.user_id}`}
      className="flex w-full items-center gap-3 rounded-lg border border-slate-100 px-3 py-2 text-left transition-colors hover:border-primary/40 hover:bg-primary/5 disabled:opacity-60">
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-medium text-slate-800">{u.name}</div>
        <div className="truncate text-[11px] text-slate-400">
          {u.email}{u.org_name ? ` · ${u.org_name}` : ""} · {u.role}
        </div>
      </div>
      {busy === u.user_id && <Loader2 className="h-4 w-4 shrink-0 animate-spin text-primary" />}
      <span onClick={(e) => togglePin(u, e)} data-testid={`view-as-pin-${u.user_id}`}
        className="shrink-0 rounded p-1 text-slate-300 hover:bg-slate-100 hover:text-amber-500"
        title={pinned ? "Remove shortcut" : "Pin to my menu"}>
        {pinned ? <Star className="h-4 w-4 fill-amber-400 text-amber-500" /> : <StarOff className="h-4 w-4" />}
      </span>
    </button>
  );

  const pinIds = new Set((data?.pins || []).map((p) => p.user_id));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="view-as-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <VenetianMask className="h-4 w-4 text-amber-600" /> View as another user
          </DialogTitle>
          <DialogDescription className="text-xs">
            You keep your own identity in the audit trail. Star a user to pin them to your avatar menu.
          </DialogDescription>
        </DialogHeader>

        <div className="relative">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
          <Input autoFocus value={q} onChange={(e) => setQ(e.target.value)} data-testid="view-as-search"
            placeholder={canStaff ? "Search staff, partner or customer users…" : "Search partner or customer users…"} className="pl-8" />
        </div>

        <div className="max-h-[52vh] space-y-3 overflow-auto">
          {(data?.pins || []).length > 0 && (
            <div className="space-y-1.5">
              <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">Pinned</div>
              {data.pins.map((u) => <Row key={`pin-${u.user_id}`} u={u} pinned />)}
            </div>
          )}
          <div className="space-y-1.5">
            <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">
              {q ? "Matches" : canStaff ? "Staff, partner & customer users" : "Partner & customer users"}
            </div>
            {loading && !data ? (
              <div className="flex items-center gap-2 py-4 text-sm text-slate-400"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
            ) : (data?.results || []).length === 0 ? (
              <p className="py-4 text-center text-xs text-slate-400" data-testid="view-as-empty">No eligible users found.</p>
            ) : (
              data.results.map((u) => <Row key={u.user_id} u={u} pinned={pinIds.has(u.user_id)} />)
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ViewAsDialog;
